import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { skillModules } from '../../data/skillModules';
import { LIBRARY_PAGE_SIZE, ModuleLibrary } from '../../hooks/useModuleLibrary';
import { ModuleCard } from './ModuleCard';
import { PathwayButton } from './PathwayButton';
import { PathwayPagination } from './PathwayPagination';
import { SelectDropdown } from '../SelectDropdown';
import { ModuleSort, SORT_OPTIONS } from '../../utils/pathwayMatching';
interface LibraryResultsProps {
  library: ModuleLibrary;
  pathwayIds: string[];
  onToggleModule: (moduleId: string) => void;
  onReset: () => void;
}
export function LibraryResults({
  library,
  pathwayIds,
  onToggleModule,
  onReset
}: LibraryResultsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const total = library.results.length;
  const start = (library.page - 1) * LIBRARY_PAGE_SIZE + 1;
  const end = Math.min(library.page * LIBRARY_PAGE_SIZE, total);
  const summary = library.hasFilters ? `Showing ${start}–${end} of ${total} matching your filters, from ${skillModules.length} in the library${library.sort === 'match' && library.isRanked ? ' · ranked by keyword match' : ''}.` : `Showing ${start}–${end} of the full skill module library.`;
  return <section aria-label="Skill module library" data-id="element-1574" data-mp="3hwwdtrkgdb66">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3" data-id="element-1575" data-mp="3hxo54q70omqn">
        <div data-id="element-1576" data-mp="9iw1mld9i7ty">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1577" data-mp="9ie8vypt6bqs">
            {total} {total === 1 ? 'module' : 'modules'}
          </h2>
          {total > 0 && <p className="mt-1 text-sm text-gray-600" aria-live="polite" data-id="element-1578" data-mp="3vxz0a5fd0nhm">
              {summary}
            </p>}
        </div>
        <div className="flex items-center gap-3" data-id="element-1579" data-mp="4p1mzwcikj1q">
          {library.pageCount > 1 && <span className="text-sm text-gray-500" data-id="element-1580" data-mp="sevzgcsx85lg">
              Page {library.page} of {library.pageCount}
            </span>}
          <SelectDropdown ariaLabel="Sort modules" size="sm" menuAlign="right" value={library.sort} onChange={value => library.setSort(value as ModuleSort)} options={SORT_OPTIONS.map(option => ({
          value: option.value,
          label: `Sort: ${option.label}`
        }))} data-id="element-1581" data-mp="1735ofpumrbiu" />
        </div>
      </div>

      {total === 0 ? <div className="rounded-xl border border-gray-100 bg-white p-12 text-center shadow-sm" data-id="element-1582" data-mp="o4jrmn65c6kq">
          <SearchX size={28} className="mx-auto text-gray-400" data-id="element-1583" data-mp="jdbcdai4xqzn" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900" data-id="element-1584" data-mp="eei0fmihy81l">
            No modules match these filters
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600" data-id="element-1585" data-mp="9fx8099hksnp">
            Try removing a filter or keyword to widen the search.
          </p>
          <PathwayButton variant="secondary" size="sm" className="mt-4" onClick={onReset} data-id="element-1586" data-mp="4iez2ivgydfp">
            Reset Search
          </PathwayButton>
        </div> : <motion.div layout className="space-y-3" data-id="element-1587" data-mp="2g3nu28l9z1zq">
          {library.pageItems.map(match => <motion.div key={match.module.id} layout initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1588" data-mp="2l2qzvv92xc8r">
              <ModuleCard match={match} inPathway={pathwayIds.includes(match.module.id)} expanded={expandedId === match.module.id} onToggleExpand={() => setExpandedId(expandedId === match.module.id ? null : match.module.id)} onTogglePathway={() => onToggleModule(match.module.id)} data-id="element-1589" data-mp="1sfo7zlywrzrh" />
            </motion.div>)}
        </motion.div>}

      <PathwayPagination label="Module library pages" page={library.page} pageCount={library.pageCount} onChange={library.setPage} data-id="element-1590" data-mp="1eklry60cku0y" />
    </section>;
}